import { computed, ref } from 'vue';
import type { BookV1, Content } from '@ilteducation/content-registry-types';
import { defineStore } from 'pinia';
import { getById, getByLegacyId, getContentDownloadUrl, getCoverUrl } from './api';
import persistenceOptions from './content-store-persistence';
import type { BookWithDuplicates } from './types';

export type State = {
  books: Map<string, BookV1>;
  isFetching: boolean;
};

export type StateTree = {
  state: State;
};

const useBooks = defineStore(
  'books',
  () => {
    const state = ref<State>({
      books: new Map(),
      isFetching: false,
    });

    const getBooks = computed(() => state.value.books);
    const isFetching = computed(() => state.value.isFetching);

    const getBookById = async (bookId: string): Promise<BookV1 | undefined> => {
      if (state.value.books.has(bookId)) {
        return state.value.books.get(bookId);
      }

      state.value.isFetching = true; // should have individual flag per content?
      await getById(bookId).then((book) => state.value.books.set(book.id, book));
      state.value.isFetching = false;

      return state.value.books.get(bookId);
    };

    const getBookByLegacyId = async (bookId: string, realm: string): Promise<Content | undefined> => {
      state.value.isFetching = true;
      return getByLegacyId(bookId, realm).then((book) => {
        state.value.books.set(book.id, book);
        state.value.isFetching = false;
        return book;
      });
    };

    const getBooksByIds = async (bookIds: string[]) => {
      state.value.isFetching = true;
      const idsNotFound = bookIds.filter((id) => ![...state.value.books.keys()].includes(id));
      if (idsNotFound.length !== 0) {
        for (const id of idsNotFound) {
          try {
            await getById(id).then((book) => state.value.books.set(book.id, book));
          } catch {
            // Couldn't find book, so nothing is added to books
          }
        }
      }
      state.value.isFetching = false;
      return [...state.value.books.values()].filter((book) => bookIds.includes(book.id));
    };

    const addBooks = (entities: Content[]) => {
      entities.forEach((entity) => state.value.books.set(entity.id, entity as BookV1));
    };

    const aggregateBooksByOriginalTitle = (entities: BookV1[]) => {
      const booksByOriginalTitle: { [key: string]: BookV1[] } = {};
      return entities
        .filter((entity: BookV1) => {
          if (entity.originalTitle) {
            const key = Object.keys(booksByOriginalTitle).find((ot) =>
              ot.match(new RegExp(`${entity.originalTitle}`, 'i')),
            );
            const isFirstWithThisOriginalTitle = !key || !booksByOriginalTitle[key];

            if (!booksByOriginalTitle[key || entity.originalTitle]) {
              booksByOriginalTitle[key || entity.originalTitle] = [];
            }
            booksByOriginalTitle[key || entity.originalTitle].push(entity);

            return isFirstWithThisOriginalTitle;
          }
          return true;
        })
        .map((entity: BookV1) => ({
          ...entity,
          duplicatesCount: booksByOriginalTitle[entity.originalTitle || '']?.length || 0,
          aggregatedCategories: Array.from(
            new Set(booksByOriginalTitle[entity.originalTitle || '']?.flatMap((b) => b.categories).filter((c) => c)),
          ) as string[],
        }));
    };

    const hasDuplicates = (book: BookV1): book is BookWithDuplicates =>
      (book as BookWithDuplicates).duplicatesCount !== undefined;

    return {
      state,
      books: getBooks,
      isFetching,
      getById: getBookById,
      getByLegacyId: getBookByLegacyId,
      getByIds: getBooksByIds,
      getCoverUrl,
      getContentDownloadUrl,
      addBooks,
      aggregateBooksByOriginalTitle,
      hasDuplicates,
    };
  },
  {
    // @ts-ignore
    persist: persistenceOptions,
  },
);

export default useBooks;
